import React from 'react';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  FileSyncStatus,
  ProjectMemberRole,
  ProjectInviteStatus,
  SubscriptionTier,
  SubscriptionStatus,
} from '@/common/models/enums';

/**
 * Gets the icon component for a file sync status
 * @param status - File sync status
 * @returns React component representing the status icon, or null
 */
export function getFileSyncStatusIcon(
  status: FileSyncStatus | string
): React.ReactNode {
  switch (status) {
    case FileSyncStatus.NEW:
      return <AddIcon color="primary" />;
    case FileSyncStatus.NEWER:
    case FileSyncStatus.BEHIND:
      return <EditIcon color="info" />;
    case FileSyncStatus.DELETED:
      return <DeleteIcon color="error" />;
    default:
      return null;
  }
}

/**
 * Gets the Material-UI color for a file sync status chip/badge
 * Returns MUI color type for use with Chip component's color prop
 * @param status - File sync status
 * @returns Material-UI color string
 */
export function getFileSyncStatusColor(
  status: FileSyncStatus | string
): 'primary' | 'info' | 'warning' | 'error' | 'default' {
  switch (status) {
    case FileSyncStatus.NEW:
      return 'primary';
    case FileSyncStatus.NEWER:
      return 'info';
    case FileSyncStatus.BEHIND:
      return 'warning';
    case FileSyncStatus.DELETED:
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Get color string for file sync status (for use in sx prop)
 * Returns theme color string like 'success.main', 'warning.main', etc.
 * @param status - File sync status
 * @returns Theme color string
 */
export const getFileSyncStatusColorString = (status: string): string => {
  switch (status) {
    case FileSyncStatus.UP_TO_DATE:
      return 'success.main';
    case FileSyncStatus.BEHIND:
      return 'warning.main';
    case FileSyncStatus.NEWER:
      return 'info.main';
    case FileSyncStatus.DELETED:
      return 'error.main';
    case FileSyncStatus.NEW:
      return 'secondary.main';
    default:
      return 'text.secondary';
  }
};

/**
 * Get text label for file sync status
 */
export const getFileSyncStatusText = (status: string): string => {
  switch (status) {
    case FileSyncStatus.UP_TO_DATE:
      return 'Up to date';
    case FileSyncStatus.BEHIND:
      return 'Behind';
    case FileSyncStatus.NEWER:
      return 'Newer';
    case FileSyncStatus.DELETED:
      return 'Deleted';
    case FileSyncStatus.NEW:
      return 'New';
    default:
      return 'Unknown';
  }
};

/**
 * Get description for file sync status
 */
export const getFileSyncStatusDescription = (status: string): string => {
  switch (status) {
    case FileSyncStatus.UP_TO_DATE:
      return 'File is synchronized between local and cloud';
    case FileSyncStatus.BEHIND:
      return 'Local file needs to be updated from cloud (⬇ download)';
    case FileSyncStatus.NEWER:
      return 'Local file is newer than cloud version (⬆ upload)';
    case FileSyncStatus.NEW:
      return 'New local file not yet in cloud (⬆ upload)';
    case FileSyncStatus.DELETED:
      return 'In cloud but not locally (will be removed in the next version)';
    case FileSyncStatus.UNKNOWN:
      return 'Status cannot be determined';
    default:
      return 'Status cannot be determined';
  }
};

/**
 * Gets the Material-UI color for a project member role chip/badge
 * @param role - Project member role
 * @returns Material-UI color string ('primary' for OWNER, 'secondary' for others)
 */
export function getProjectMemberRoleColor(
  role: ProjectMemberRole | string
): 'primary' | 'secondary' {
  return role === ProjectMemberRole.OWNER ? 'primary' : 'secondary';
}

/** Invite-role descriptions only — OWNER is not invitable. */
const INVITABLE_ROLE_DESCRIPTIONS: Partial<Record<ProjectMemberRole, string>> =
  {
    [ProjectMemberRole.COLLABORATOR]: 'Can view and contribute to the project',
    [ProjectMemberRole.READ_ONLY]: 'Can view and download project files only',
  };

export function getProjectMemberRoleDescription(
  role: ProjectMemberRole | string
): string | undefined {
  return INVITABLE_ROLE_DESCRIPTIONS[role as ProjectMemberRole];
}

/**
 * Gets the Material-UI color for a project invite status chip/badge
 * PENDING: warning/yellow
 * ACCEPTED: success/green
 * DECLINED: error/red
 * EXPIRED: default/gray
 * @param status - Project invite status
 * @returns Material-UI color string
 */
export function getProjectInviteStatusColor(
  status: ProjectInviteStatus | string
): 'warning' | 'success' | 'error' | 'default' {
  switch (status) {
    case ProjectInviteStatus.PENDING:
      return 'warning';
    case ProjectInviteStatus.ACCEPTED:
      return 'success';
    case ProjectInviteStatus.DECLINED:
      return 'error';
    case ProjectInviteStatus.EXPIRED:
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get MUI color for subscription tier
 * Returns color compatible with Chip component
 */
export const getSubscriptionTierColor = (
  tier: string
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error' => {
  switch (tier.toLowerCase()) {
    case SubscriptionTier.FREE:
      return 'default';
    case SubscriptionTier.BASIC:
      return 'info';
    case SubscriptionTier.PRO:
      return 'primary';
    case SubscriptionTier.ENTERPRISE:
      return 'secondary';
    default:
      return 'default';
  }
};

/**
 * Get MUI color for subscription status
 * Returns color compatible with Chip component
 */
export const getSubscriptionStatusColor = (
  status: string
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error' => {
  switch (status.toLowerCase()) {
    case SubscriptionStatus.ACTIVE:
      return 'success';
    case SubscriptionStatus.TRIALING:
      return 'info';
    case SubscriptionStatus.PAST_DUE:
      return 'warning';
    case SubscriptionStatus.CANCELLED:
      return 'error';
    default:
      return 'default';
  }
};
